import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { AudioLines, FolderOpen, Plus, Settings, Wallet } from 'lucide-react'
import { motion } from 'motion/react'

import { cn } from '@/lib/utils'

export const Route = createFileRoute('/dashboard')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' })
    }
  },
  component: DashboardPage,
})

interface AppItem {
  label: string
  icon: typeof Plus
  href: string
  gradient: string
  isAvailable: boolean
}

const APPS: AppItem[] = [
  {
    label: 'Create',
    icon: Plus,
    href: '/create',
    gradient: 'from-violet-500 to-purple-600',
    isAvailable: true,
  },
  {
    label: 'My Projects',
    icon: FolderOpen,
    href: '/projects',
    gradient: 'from-blue-500 to-cyan-500',
    isAvailable: true,
  },
  {
    label: 'Audio Library',
    icon: AudioLines,
    href: '/audio-library',
    gradient: 'from-orange-400 to-rose-500',
    isAvailable: true,
  },
  {
    label: 'Top Up',
    icon: Wallet,
    href: '/top-up',
    gradient: 'from-emerald-400 to-teal-500',
    isAvailable: true,
  },
  {
    label: 'Settings',
    icon: Settings,
    href: '/settings',
    gradient: 'from-zinc-400 to-zinc-500',
    isAvailable: true,
  },
]

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
}

const fadeUp = {
  hidden: { opacity: 0, y: 24, scale: 0.92 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as const },
  },
}

function DashboardPage() {
  return (
    <div className="relative flex min-h-screen items-center justify-center px-4 sm:px-6">
      <motion.div
        className="absolute top-8 left-1/2 flex -translate-x-1/2 flex-col items-center sm:top-10"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <img src="/logo-full.png" alt="Melodex" className="h-14 sm:h-28" />
        <h1 className="mt-1 whitespace-nowrap text-sm font-medium text-muted-foreground sm:-mt-3 sm:text-base">
          What will you create today?
        </h1>
      </motion.div>

      <motion.div
        className="grid w-full max-w-xs grid-cols-3 justify-items-center gap-y-8 gap-x-4 sm:max-w-none sm:w-auto sm:grid-cols-5 sm:gap-12"
        variants={stagger}
        initial="hidden"
        animate="visible"
      >
        {APPS.map((app) => {
          const Icon = app.icon
          return (
            <motion.div key={app.label} variants={fadeUp}>
              {app.isAvailable ? (
                <Link
                  to={app.href}
                  className="group flex flex-col items-center gap-2 sm:gap-4"
                >
                  <div
                    className={cn(
                      'flex size-20 items-center justify-center rounded-[22%] bg-gradient-to-br shadow-card transition-all duration-200 group-hover:-translate-y-1 group-hover:shadow-card-hover sm:size-36',
                      app.gradient,
                    )}
                  >
                    <Icon
                      className="size-8 text-white sm:size-14"
                      strokeWidth={1.6}
                    />
                  </div>
                  <span className="text-center text-xs font-medium sm:text-base">
                    {app.label}
                  </span>
                </Link>
              ) : (
                <div className="flex cursor-default flex-col items-center gap-2 opacity-50 sm:gap-4">
                  <div
                    className={cn(
                      'flex size-20 items-center justify-center rounded-[22%] bg-gradient-to-br shadow-card sm:size-36',
                      app.gradient,
                    )}
                  >
                    <Icon
                      className="size-8 text-white sm:size-14"
                      strokeWidth={1.6}
                    />
                  </div>
                  <span className="text-center text-xs font-medium sm:text-base">
                    {app.label}
                  </span>
                </div>
              )}
            </motion.div>
          )
        })}
      </motion.div>
    </div>
  )
}
